import { Injectable, Injector } from '@angular/core';
import { NavigationItem } from './NavigationItem';
import { ContentService } from '../content/content.service';
import { MessengerService } from '../messenger/messenger.service';
import { PagesByContentType } from '../../maps/pages.map';

import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ContentPipeService } from '../content-pipe/content-pipe.service';
import { Models } from '@ibexfusion/models';
import { IntervalService } from '../interval/interval.service';
import { ConnectionService } from '../connection/connection.service';
import { TranslateService } from '../translate/translate.service';
import { HelperService } from '../helper/helper.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationItemService {

  navigationItems: NavigationItem[] = [];
  flatNavigationItems: NavigationItem[] = [];
  metaPackage: any;
  public $ready = new BehaviorSubject<any>(null);
  public $widgetItems = new BehaviorSubject<any>(null);
  public $hasRecommendation = new BehaviorSubject<any>(null);
  public widgetItems: any;
  private id = 0;
  private initMenuRunning = false;
  private lastMenuItems: string;

  constructor(private contentService: ContentService, private injector: Injector, private connection: ConnectionService, private translate: TranslateService) {
  }

  public async initMenu(items: any, force = false, noSubscription = false): Promise<NavigationItem[]> {
    if (this.lastMenuItems === JSON.stringify(items) && !force) {
      return;
    }
    this.lastMenuItems = JSON.stringify(items);
    if (this.initMenuRunning) {
      console.log('initMenu is already running, waiting...');
      while (this.initMenuRunning) {
        await HelperService.delay(500);
      }
      return this.initMenu(items, force, noSubscription);
    }

    this.initMenuRunning = true;

    try {
      if (!force) {
        try {
          if (this.navigationItems.length && !window.location.href.endsWith('/home')) {
            return;
          }
        } catch (error) {
          console.error("Ein unerwarteter Fehler ist aufgetreten:", error);
        }
      }
      this.id = 0;
      const navigationItemsCopy = JSON.parse(JSON.stringify(this.flatNavigationItems));
      this.navigationItems = [];
      this.flatNavigationItems = [];
      this.metaPackage = this.contentService.metaPackage;

      const mainMenuItems = items.itemApps;
      const widgetMenuItems = items.itemWidgets;
      const navigationItems = await this.createDynamicItems(mainMenuItems, 1);
      this.navigationItems = _.uniqBy(this.navigationItems.concat(navigationItems), 'id');
      this.flatNavigationItems = [];
      this.flatItems(this.navigationItems);
      const widgetItems = [];
      for (const item of (widgetMenuItems?.children || []).filter(item => !!this.contentService.getMenuItem(item.id))) {
        const widgetItemId = item.id;
        const widgetItem = this.contentService.getMenuItem(widgetItemId);
        const widgetEntry = this.contentService.getEntry(widgetItem?.entry);
        let id = widgetItemId.substr(0, widgetItemId.indexOf('_widget') > -1 ? widgetItemId.indexOf('_widget') : widgetItemId.indexOf('_Widget'));
        let navItem = _.find(this.navigationItems, (elem: any) => {
          return !!elem.contentId && elem.contentId.indexOf(id) > -1;
        });
        if (!navItem && !this.isSecuredWidgetItem(widgetItemId)) {
          id = id.split('_')[id.indexOf('_item') > -1 ? 0 : 1];
          navItem = _.find(this.navigationItems, (elem: any) => {
            return !!elem.contentId && elem.contentId.indexOf(id) > -1;
          });
        }
        if (navItem) {
          if (!!widgetEntry) {
            widgetItem.entry = widgetEntry as any;
          }
          widgetItems.push({ item: widgetItem, navigationItem: navItem });
        }
      }
      if (this.compareItemsWithoutLiveItems(this.widgetItems, widgetItems) && widgetItems?.length > 0) {
        this.widgetItems = _.uniqBy(widgetItems, 'item._id');
        if (!noSubscription) {
          this.$widgetItems.next(null);
          setTimeout(() => {
            this.$widgetItems.next(_.uniqBy(widgetItems, 'item._id'));
          }, 50);
        }
      }
      if (JSON.stringify(this.flatNavigationItems) !== JSON.stringify(navigationItemsCopy) && this.flatNavigationItems?.length > 0 && !noSubscription) {
        this.$ready.next(Date.now());
      }

      IntervalService.set('initMenuInterval', () => {
        this.initMenu(items);
      }, 1000 * 67);

      return this.fixOrders(this.navigationItems);

    } catch (error) {
      console.error("Error in initMenu:", error);
    } finally {
      this.initMenuRunning = false;
    }
  }

  private fixOrders(items) {
    for (let item of items) {
      switch (item?.page) {
        case 'gst':
          for (let child of item?.children) {
            if (child?.entry?.related?.entries) {
              for (const related of child.entry.related.entries) {
                related['entry'] = this.contentService.getById(related.id);
              }
              child.entry.related.entries = child.entry.related.entries.sort((a, b) => new Date(a?.entry?.detail?.date).getTime() - new Date(b?.entry?.detail?.date).getTime());
            }
          }
          item.children = item.children.sort((a, b) => new Date(a?.entry?.related?.entries?.[0]?.entry?.detail?.date).getTime() - new Date(b?.entry?.related?.entries?.[0]?.entry?.detail?.date).getTime());
          break;
      }
    }
    return items;
  }
  private compareItemsWithoutLiveItems(a, b) {
    a = _.cloneDeep(a);
    b = _.cloneDeep(b);
    if (!a || !b) {
      return true;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i].navigationItem && a[i].navigationItem.live) {
        a.splice(i, 1);
        i--;
      }
    }
    for (let i = 0; i < b.length; i++) {
      if (b[i].navigationItem && b[i].navigationItem.live) {
        b.splice(i, 1);
        i--;
      }
    }
    return JSON.stringify(a) !== JSON.stringify(b);
  }

  /* dynamic items from package */

  public async createDynamicItems(menuItem: any, level: number, parent: any = null): Promise<NavigationItem[]> {
    const navigationItems: NavigationItem[] = [];
    if (menuItem?.children?.length) {
      menuItem.children = _.uniqBy(menuItem.children, 'id');
      for (const child of menuItem?.children || []) {
        const data = this.prepareItem(child.id);
        if (data && (<any>data).isSecured) {
          //todo: @raphael skip when secured since touchscreen
        } else if (data && data['contentType'] === 'code-snippet') {
          const navigationItem: NavigationItem = this.createCodeSnippetItem(data.menuEntry, data.page);
          if (navigationItem.entry && navigationItem.entry['navigationItem'] && navigationItem.entry['navigationItem'].live) {
            const children = _.clone(navigationItem.entry['navigationItem'].menuItems);
            for (let element of navigationItem.entry['navigationItem'].menuItems) {
              try {
                const res = await this.connection.get(element.url);
                for (let i = 0; i < children.length; i++) {
                  if (children[i].url === element.url) {
                    children[i] = await this.createLiveItemWithChildren(children[i], res, level, navigationItem);
                  }
                }
              } catch (err) {
                console.warn('could not request live data');
              }
            }
            if (children.length === 1 && navigationItem.entry['navigationItem'].skipFirst && children[0] && children[0].children) {
              navigationItem.children = children[0].children;
              for (let child of navigationItem.children) {
                child.level--;
                child.parentId = navigationItem.id
                child.children = child.children || [];
              }
            } else {
              navigationItem.children = children;
            }
          }
          navigationItems.push(navigationItem);
          // todo if children: createCodeSnippet for children
        } else if (data) {
          if (!!this.hasContent(data)) {
            const navigationItem: NavigationItem = this.createItem(data, level, parent);
            if (navigationItem) {
              navigationItems.push(navigationItem);
              if (data?.menuItem?.children?.length) {
                const subNavigationItems = await this.createDynamicItems(data.menuItem, level + 1, navigationItem);
                navigationItem.children = subNavigationItems;
                const index = _.findIndex(navigationItems, (item: any) => item.id === navigationItem.id);
                navigationItems[index].children = subNavigationItems;
              }
            }
          }
        }
      }
    }
    return navigationItems;
  }

  public prepareItem(id: string) {
    const menuItem = this.contentService.getMenuItem(id);
    if (!menuItem) { return null; }
    const menuEntry = this.contentService.getEntry(menuItem.entry);
    if (!menuEntry || menuItem.isSecured) {
      return null;
    }

    if (!menuEntry.detail.contentType && menuEntry.detail.type === 'cross-country') {
      menuEntry.detail.contentType = menuEntry.detail.type; // todo @Raphael packageJob
    }
    if (!menuEntry.detail.contentType && menuEntry.detail.type === 'hiking') {
      menuEntry.detail.contentType = 'hiking'; // todo @Raphael packageJob
    }

    if ([menuEntry.detail.contentType, menuEntry.detail.appType].includes('recommendations') && menuEntry.detail.contentUrl) {
      this.$hasRecommendation.next(menuEntry);
    }

    const contentType = menuEntry.detail.contentType || menuEntry.detail.appType;
    let page = PagesByContentType.get(contentType);
    if (contentType === 'code-snippet' && menuEntry.detail.page) {
      page = PagesByContentType.get(menuEntry.detail.page);
    }

    if (contentType && !page) {
      return null;
    }
    if (!page && (!menuItem.children || !menuItem.children.length)) {
      return null;
    }

    return { menuItem, menuEntry, page, contentType };
  }

  public createItem(data: any, level: number, parent: any): NavigationItem {
    let path = data.page + '/' + this.prettifyPath(data.menuEntry.title);
    if (parent && parent.title) {
      path = data.page + '/' + this.prettifyPath(parent.title) + '/' + this.prettifyPath(data.menuEntry.title);
    }
    if (data.page === 'prs' && parent && !data?.menuEntry?.related?.entries?.length) {
      return null;
    }
    return {
      id: (this.id++).toString(), contentId: data.menuItem._id, title: data.menuEntry.title, level, entry: data.menuEntry, children: [],
      icon: data.menuEntry.detail.tweebieIcon || null, page: data.page || null, path, parentId: parent ? parent.id : null
    } as NavigationItem;
  }

  private createLiveItemWithChildren(element, childData, level, parent) {
    if (typeof element.title !== 'string' && element.title[this.translate.getCurrentLanguage()]) {
      element.title = element.title[this.translate.getCurrentLanguage()];
    }
    element.id = (this.id++).toString();
    element.level = level;
    element.path = element.page + '/' + this.prettifyPath(parent.title) + '/' + this.prettifyPath(element.title);
    element.parentId = parent.id;
    element.entry = { title: element.title, detail: { contentType: element.contentType } };
    element.children = [];
    for (let item of childData) {
      if (typeof item.title !== 'string' && item.title[this.translate.getCurrentLanguage()]) {
        item.title = item.title[this.translate.getCurrentLanguage()];
      }
      item.parentId = element.id;
      item.id = (this.id++).toString();
      item.entry = _.clone(item);
      item.entry.detail = _.clone(item);
      delete item.entry.detail.entry;
      item.path = element.page + '/' + this.prettifyPath(element.title) + '/' + this.prettifyPath(item.title);
      item.level = level + 1;
      element.children.push(item);
    }
    if (element.skipFirst && element.children.length === 1) {
      element.entry.detail = _.merge(_.clone(element.children[0]), element.entry.detail);
      element.children = [];
    }
    return element;
  }

  public initItemByPath(path: string, parentUrl?: string, retried = false): NavigationItem {
    if (!this.flatNavigationItems || !path) {
      return null;
    }
    let item;
    if (parentUrl) {
      parentUrl = parentUrl.replace(/^\//, ''); // remove leading slash
      parentUrl = parentUrl.replace(/\/snv-dta$/, '/snv'); // if we are looking for a detail not for sub-nav
      const parent = _.find(this.flatNavigationItems, (navItem: any) => navItem.path.indexOf('/' + parentUrl) > -1);
      if (parent && parent.children && _.find(parent.children, (navItem: any) => navItem.path === path)) {
        item = _.find(parent.children, (navItem: any) => navItem.path === path);
      }
    }
    if (!item) {
      path = path.replace(/^\//, ''); // remove leading slash
      path = path.replace(/\/snv-dta$/, '/snv'); // if we are looking for a detail not for sub-nav
      item = _.find(this.flatNavigationItems, (navItem: any) => navItem.path === path);
    }
    if (!item && /\/dta\//.test(path)) {
      const parentRoute = path.replace(/\/dta\/.*$/, '');
      return this.initItemByPath(parentRoute);
    }
    if (!item && !retried) {
      this.flatItems(this.navigationItems);
      return this.initItemByPath(path, parentUrl, true);
    }
    return item;
  }

  public initMainItemByTitle(title: string): NavigationItem {
    if (!this.navigationItems || !title) {
      return null;
    }
    return _.find(this.navigationItems, (navItem: any) => this.prettifyPath(navItem.title) === title || navItem.title === title || navItem.title === this.prettifyPath(title) || this.prettifyPath(navItem.title) === this.prettifyPath(title));
  }

  public getItemById(id: string): NavigationItem {
    if (!this.flatNavigationItems) {
      return null;
    }
    return this.flatNavigationItems.find((navItem: any) =>
      navItem.id === id || navItem.conetntId === id || (id.length > 4 && (navItem.id?.includes(id) || navItem.contentId?.includes(id))) ||
      (id.includes('_Widget') && (navItem.id?.includes(id.replace('_Widget', '')) || navItem.contentId?.includes(id.replace('_Widget', ''))))
    ) || null;
  }

  public getItemByPath(path: string): NavigationItem {
    return _.find(this.navigationItems, (navItem: any) => navItem.path === path);
  }

  /* get item -  ! use them only after $ready is true ! */

  public flatItems(items: any) {
    this.flatNavigationItems = this.flatNavigationItems.concat(items);
    const children = _.flatMap(items, (item: any) => {
      return item && item.children ? item.children : [];
    });
    if (children && children.length) {
      this.flatItems(children);
    }
  }

  public getRootTitle(id: string): string {
    const item = id ? this.getItemById(id) : null;
    if (item && item.level === 1) {
      return item.title;
    } else if (item && item.parentId) {
      return this.getRootTitle(item.parentId);
    }
    return null;
  }

  public getRootItem(id: string, level: number): string {
    const item = id ? this.getItemById(id) : null;
    if (item && item.level === level) {
      return item.id;
    } else if (item && item.parentId) {
      return this.getRootItem(item.parentId, level);
    }
    return null;
  }

  public getHeaderContent(item: any, entry: any = null) {
    if (!item) {
      return null;
    }
    const rootTitle = this.getRootTitle(item.parentId);
    if (entry) {
      return {
        title: rootTitle || item.title,
        subtitle: entry.title
      };
    }
    return {
      title: rootTitle || item.title,
      subtitle: rootTitle ? item.title : null,
    };
  }

  /* external menu-control functions */

  public toLowerCaseFirstLetter(pageName: string) {
    if (!pageName || !pageName.length) {
      return pageName;
    }
    return pageName.charAt(0).toLowerCase() + pageName.slice(1);
  }

  public prettifyPath(val: string) {
    let title = JSON.parse(JSON.stringify(val));
    if (typeof title !== 'string') {
      title = title[window['masterLanguage']] || title['de'];
    }
    title = title.trim();
    const umlautsMap = {
      'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss', 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue', 'é': 'ee', 'è': 'eg', 'ç': 'ce', 'É': 'Ee', 'È': 'Eg',
      'Ç': 'Ce', 'Ú': 'Ue', 'ú': 'ue', 'Á': 'Ae', 'á': 'ae', 'Ù': 'Ug', 'ù': 'ug', 'À': 'Ag', 'à': 'ag', 'Â': 'A', 'â': 'a', 'Ô': 'o',
      'ô': 'o', 'ị': 'i', 'Ị': 'I', 'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i', 'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I'
    };

    title = title.replace(/\s?[\/&+—–-—-⸺]\s?/g, '-').replace(/[?.,!:;()"#'«»`]/g, '').replace(/\u200B/g, '')
      .replace(/\s/g, '_').replace(/[äöüßÄÖÜéèçÉÈÇÚúÁáÙùÀàÂâÔôịỊìíîïÌÍÎÏ]/g, match => umlautsMap[match] || match);
    title = encodeURIComponent(title);
    title = title.replace(/%[0-9A-Fa-f]{2}/g, '');
    return title;
  }

  private isSecuredWidgetItem(id: string) {
    id = id.replace('_Widget', '_App');
    id = id.replace('_widgetItem', '');
    const menuItem = this.contentService.getMenuItem(id);

    if (!menuItem) { return null; }
    return !!menuItem.isSecured;
  }

  private createSecuredItem(data: any, level: number, parent: any) {
    const navigationItem: NavigationItem = {
      id: (this.id++).toString(), contentId: data.menuItem._id, title: 'Secured app',
      level, entry: null, children: [], icon: null, page: null, path: null, parentId: parent ? parent.id : null, isSecured: true
    };

    return navigationItem;
  }

  /* helpers */

  private createCodeSnippetItem(menuEntry: any, page: string): NavigationItem {
    if (menuEntry && menuEntry.detail && menuEntry.navigationItem && menuEntry.navigationItem.targets && menuEntry.navigationItem.targets.indexOf('ibextouch') === -1) {
      return;
    }
    return {
      id: (this.id++).toString(), title: menuEntry.title, level: 1, icon: menuEntry.detail.tweebieIcon || null, children: [], page,
      live: !!menuEntry?.detail?.navigationItem?.live, entry: menuEntry.detail || null,
      path: page, contentId: menuEntry._id
    } as NavigationItem;
  }

  private hasContent(data: any) {
    if (!data) {
      return false;
    }
    let useItem = true;
    if (!data.menuItem?.children || !data.menuItem?.children?.length) {
      switch (data.page) {
        case 'wcm':
          if (!data?.menuEntry?.related?.entries?.length) {
            useItem = false;
          }
          break;
        case 'evt':
          const events = this.injector.get(ContentPipeService).getTransformedEntry<Models.EventEntry>(data.menuEntry);
          const now = Math.floor(Date.now() / 1000);
          const hasValidRange = Array.isArray(events) && events.length > 0 && events.some(e => (e.dateRanges || []).some(r => r?.end >= now));
          const allHaveNoDateRanges = Array.isArray(events) &&
            events.length > 0 && events.every(e => !Array.isArray(e.dateRanges) || e.dateRanges.length === 0);
          if (!events || (!Array.isArray(events) && !this.hasItemContent(events)) || (!allHaveNoDateRanges && !hasValidRange)) {
            useItem = false;
          }
          break;
        case 'rst': case 'acm': case 'nws':
          const item = this.injector.get(ContentPipeService).getTransformedEntry<Models.EventEntry>(data.menuEntry);
          if (!item || (!Array.isArray(item) && !this.hasItemContent(item))) {
            useItem = false;
          }
          break;
        case 'cin':
          const content = this.injector.get(ContentPipeService).getTransformedEntry<Models.CinemaEntry>(data.menuEntry);
          useItem = !!content && !!content.movies && !!content.movies.length;
          break;
        case 'gst':
          useItem = !!data?.menuEntry?.related?.entries?.map(el => this.contentService.getEntry(el.id)).filter(el => !!el)?.length;
          break;
      }
    } else {
      switch (data.page) {
        case 'cin':
          const content = this.injector.get(ContentPipeService).getTransformedEntry<Models.CinemaEntry>(data.menuEntry);
          useItem = !!content && !!content.movies && !!content.movies.length;
          break;
        case 'ifo':
          let tempUseItem = false;
          if (data && data.menuItem && data.menuItem.children) {
            for (let child of data.menuItem.children) {
              if (this.contentService.getEntry(this.contentService.getMenuItem(child.id)?.entry)) {
                tempUseItem = true;
              }
            }
          }
          if (data && !data.menuEntry && !data.menuEntry.related && !data.menuEntry.related.entries) {
            for (let element of data.menuEntry.related.entries) {
              if (this.contentService.getEntry(element.id)) {
                tempUseItem = true;
              }
            }
          }
          useItem = tempUseItem;
          break;
        case 'gst':
          if (data?.menuItem?.children?.length === 1) {
            useItem = this.hasContent(this.prepareItem(data.menuItem.children[0].id));
          }
          break;
      }
    }
    return useItem;
  }

  private hasItemContent(item) {
    const media = item.media && item.media.length ? item.media : [];
    const documents = item.documents && item.documents.length ? item.documents : [];
    const hasContact = !!item.phone || !!item.email;
    const hasAddress = !!item.street || (!!item.zip && !!item.city);
    const hasLink = !!item.link || !!item.bookingUrl || !!item.website;
    return !!hasAddress || !!hasContact || (media?.length === 0 ? documents.length > 1 : documents.length >= 1) ||
      (!!item.description && !!item.description.length) || (!!item.textContent && !!item.textContent.length) ||
      !!item.openingHours || !!hasLink;
  }
}
